// v0.4.51 — Step 6 country-return ChecklistRow (operator sign-off q1=a, q2=a, q3=a).
//
// Embeds the existing CountryReturnTabs as the sublabel of a Step 6
// ChecklistRow so the filing operator sees per-country deep-links
// next to the freeze / sign-off / OSS-lint rows.
//
//   q1=a — embed CountryReturnTabs inside a ChecklistRow.
//   q2=a — done=true only when every active country.ready_to_file
//          is true. Same source of truth as the XLSX export gate.
//   q3=a — hide when no country has activity (same as the inline
//          surface on the calc page).
import React, { useEffect, useState } from "react";
import { CountryReturnTabs } from "./CountryReturnTabs";
import { InfoCircle } from "./InfoCircle";
import { getCountryReturnSummary } from "../lib/api";
import { useTranslation } from "react-i18next";

export const CountryReturnsChecklistRow = ({ calcId, position = 4 }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!calcId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const s = await getCountryReturnSummary(calcId);
        if (!cancelled) setSummary(s);
      } catch {
        // A2-537 (Antigravity UX review U8, 2026-10-25): this was silent, so
        // the row vanished on a calc WITH country returns and the checklist
        // read 1, 2, 3, 5. A failed load is said, not hidden.
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [calcId]);

  if (failed) {
    return (
      <div data-testid="step-6-country-returns-row" data-state="unavailable"
        className="flex items-start gap-3 px-4 py-3 border rounded-xl border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
        <span className="text-lg text-amber-600 dark:text-amber-400">⚠</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-amber-900 dark:text-amber-100">{`${position}. Country VAT returns`}{/* i18n-ignore: wording pinned by test_v0493_followup_cleanup */}</div>
          <div data-testid="step-6-country-returns-sublabel" className="text-xs mt-0.5 text-amber-900 dark:text-amber-100">
            {t("ui.country_return.checklist_unavailable", { defaultValue: "Could not load the country returns. Open each one from Step 4 before you file." })}
          </div>
        </div>
      </div>
    );
  }
  // q3 — hide when no country has activity.
  if (!summary || !summary.countries || summary.countries.length === 0) {
    return null;
  }

  // q2 — done iff every active country.ready_to_file is true.
  const allReady = summary.countries.every((c) => c.ready_to_file);
  const blocked = summary.countries.filter((c) => !c.ready_to_file);
  const sublabel = allReady
    ? "Every active country return reconciles — ready to file."
    : `${blocked.length} country return(s) failed reconciliation — do not file until resolved.`;

  return (
    <div
      data-testid="step-6-country-returns-row"
      data-state={allReady ? "ready" : "needs-action"}
      className={`flex items-start justify-between gap-3 px-4 py-3 border rounded-xl ${
        allReady
          ? "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 opacity-90"
          : "border-2 border-rose-400 dark:border-rose-600 border-l-8 border-l-rose-600 dark:border-l-rose-500 bg-rose-50 dark:bg-rose-950/30 shadow-md shadow-rose-500/10"
      }`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span
          data-testid="step-6-country-returns-marker"
          className={`text-lg ${allReady ? "text-emerald-500 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400 font-bold"}`}
        >
          {allReady ? "✓" : "⚠"}
        </span>
        <div className="flex-1 min-w-0">
          <div className={`tracking-normal ${
            allReady ? "text-sm text-slate-500 dark:text-slate-400" : "text-base text-rose-800 dark:text-rose-100 font-bold"
          }`}>
            {`${position}. Country VAT returns`}{/* i18n-ignore: wording pinned by test_v0493_followup_cleanup */}
          </div>
          <div
            data-testid="step-6-country-returns-sublabel"
            className={`text-[11px] mt-0.5 flex items-center gap-1.5 ${
              allReady ? "text-slate-500 dark:text-slate-400" : "text-rose-800 dark:text-rose-100 font-semibold"
            }`}
          >
            <span>{sublabel}</span>
            {!allReady && (
              <InfoCircle testid="step-6-country-returns-help" align="right">
                <div className="font-semibold text-slate-900 dark:text-slate-50 mb-1">
                  {t("ui.country_return.chip_help.title", { defaultValue: "What happens when you click a chip?" })}
                </div>
                <div className="text-slate-700 dark:text-slate-200 mb-2">
                  {t("ui.country_return.chip_help.p1_before", { defaultValue: "The chips don't fix the breach themselves — they take you to the breached return so you can see" })} <span className="font-semibold">{t("ui.country_return.chip_help.p1_why", { defaultValue: "why" })}</span> {t("ui.country_return.chip_help.p1_after", { defaultValue: "it failed and pick the right fix." })}
                </div>
                <div className="text-slate-700 dark:text-slate-200 mb-2 font-semibold">
                  {t("ui.country_return.chip_help.path", { defaultValue: "Resolution path:" })}
                </div>
                <ol className="text-slate-700 dark:text-slate-200 list-decimal pl-5 space-y-1 mb-2">
                  <li>
                    {t("ui.country_return.chip_help.step1_before", { defaultValue: "Click a red" })} <span>⚠</span> {t("ui.country_return.chip_help.step1_mid", { defaultValue: "chip (or its" })}{" "}
                    <span className="font-semibold">
                      {t("ui.country_return.chip_help.open_and_review", { defaultValue: "Open and review →" })}
                    </span>{" "}
                    {t("ui.country_return.chip_help.step1_after", { defaultValue: "button) — that opens the country return." })}
                  </li>
                  <li>
                    {t("ui.country_return.chip_help.step2", { defaultValue: "Read the \"Cannot file\" banner — it states exactly what failed (unknown rate, missing cadence, missing input VAT, etc.)." })}
                  </li>
                  <li>
                    {t("ui.country_return.chip_help.step3_before", { defaultValue: "For a flagged row, use" })}{" "}
                    <span className="font-semibold">
                      {t("ui.country_return.review_txn_single", { defaultValue: "Review 1 transaction →" })}
                    </span>{" "}
                    {t("ui.country_return.chip_help.step3_mid", { defaultValue: "to see the source row, or" })}{" "}
                    <span className="font-semibold">
                      {t("ui.country_return.report_rate_cta", { defaultValue: "Report rate to maintainer →" })}
                    </span>{" "}
                    {t("ui.country_return.chip_help.step3_after", { defaultValue: "if the rate is genuinely new and the rate table needs patching." })}
                  </li>
                  <li>
                    {t("ui.country_return.chip_help.step4", { defaultValue: "Apply the fix at source, re-run the calculation, and come back here — once every chip is green, the spreadsheet export unlocks automatically." })}
                  </li>
                </ol>
                <div className="border-t border-slate-200 dark:border-slate-700 pt-2 text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
                  {t("ui.country_return.chip_help.footer_before", { defaultValue: "The chip's job is" })}{" "}
                  <span className="font-semibold">{t("ui.country_return.chip_help.footer_nav", { defaultValue: "navigation" })}</span>{t("ui.country_return.chip_help.footer_after", { defaultValue: ", not resolution. The actual fix always happens on the country-return page." })}
                </div>
              </InfoCircle>
            )}
          </div>
          <div className="mt-2">
            <CountryReturnTabs
              calcId={calcId}
              countries={summary.countries}
              active={null}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryReturnsChecklistRow;
